import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { ValueFormatterService } from '@wilson/ag-grid/service';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { FilterKey, FilterModule } from '@wilson/filter';
import { QualificationTrainings } from '@wilson/interfaces';
import { PageFrameV2Component } from '@wilson/page-frame';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { BehaviorSubject, map, Observable, take } from 'rxjs';
import { TrainingQualificationsBulkEditDrawerComponent } from './training-qualifications-bulk-edit-drawer/training-qualifications-bulk-edit-drawer.component';
import { TrainingQualificationCreateDrawerComponent } from './training-qualifications-create-drawer/training-qualifications-create-drawer.component';
import { TrainingQualificationsFinalizeDrawerComponent } from './training-qualifications-finalize-drawer/training-qualifications-finalize-drawer.component';
import { QualificationTrainingsV2ListComponent } from './training-qualifications-v2-list/qualification-trainings-v2-list.component';
import { TrainingQualificationsFilterDrawerComponent } from './training-qualifications-v2-list/training-qualifications-filter-drawer/training-qualifications-filter-drawer.component';
import { TrainingQualificationBulkEditTriggerComponent } from './trigger/training-qualification-bulk-edit-trigger.component';
import { TrainingQualificationCreateTriggerComponent } from './trigger/training-qualification-create-trigger.component';
import { TrainingQualificationDeleteTriggerComponent } from './trigger/training-qualification-delete-trigger.component';
import { TrainingQualificationFinalizeTriggerComponent } from './trigger/training-qualification-finalize-trigger.component';
import { FilterValues } from '../../types';

@Component({
  selector: 'wilson-qualification-trainings-v2-overview',
  imports: [
    CommonModule,
    FeatureFlagsModule,
    FilterModule,
    FontAwesomeModule,
    NzBadgeModule,
    NzButtonModule,
    PageFrameV2Component,
    QualificationTrainingsV2ListComponent,
    TrainingQualificationBulkEditTriggerComponent,
    TrainingQualificationCreateDrawerComponent,
    TrainingQualificationCreateTriggerComponent,
    TrainingQualificationDeleteTriggerComponent,
    TrainingQualificationFinalizeTriggerComponent,
    TrainingQualificationsBulkEditDrawerComponent,
    TrainingQualificationsFilterDrawerComponent,
    TrainingQualificationsFinalizeDrawerComponent,
    TranslatePipe,
  ],
  providers: [ValueFormatterService],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './qualification-trainings-v2-overview.component.html',
})
export class QualificationTrainingsV2OverviewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  searchFilter = new BehaviorSubject<string>('');
  selectedQualifications: QualificationTrainings[] = [];
  statusFilter = new BehaviorSubject<string[]>([]);
  usersFilter = new BehaviorSubject<string[]>([]);
  qualificationFilter = new BehaviorSubject<string[]>([]);
  refreshSubject = new BehaviorSubject<number>(0);
  isDrawerVisible = false;
  isCreateDrawerVisible = false;
  faFilter = faFilter;

  protected filterKeys: FilterKey[] = [
    'qualificationTrainingsStatus',
    'users',
    'qualificationV2',
  ];

  private readonly defaultStatusFilters = [
    'booked',
    'cancelled',
    'completedFailed',
    'completedSuccess',
    'declined',
    'inProgress',
    'open',
    'paused',
    'pending',
    'resultAccepted',
    'resultRejected',
  ];

  ngOnInit(): void {
    this.numberOfAppliedFilters$ = this.route.queryParams.pipe(
      map(
        (params) =>
          this.filterKeys.filter((key) => params[key] !== undefined).length,
      ),
    );

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      if (Object.keys(params).length === 0) {
        this.setDefaultStatusFilters();
      }
    });
  }

  private setDefaultStatusFilters(): void {
    const queryParams = {
      qualificationTrainingsStatus: this.defaultStatusFilters.join(','),
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  protected numberOfAppliedFilters$!: Observable<number>;

  onSearchChange(event: string): void {
    this.searchFilter.next(event);
  }

  onSelectedRowsChange(rows: QualificationTrainings[]): void {
    this.selectedQualifications = rows;
  }

  openDrawer(): void {
    this.isDrawerVisible = true;
  }

  onApplyFilters(filters: FilterValues): void {
    this.statusFilter.next(filters.status);
    this.usersFilter.next(filters.userIds);
    this.qualificationFilter.next(filters.qualificationIds);
  }

  openCreateDrawer(): void {
    this.isCreateDrawerVisible = true;
  }

  onSave(): void {
    this.isCreateDrawerVisible = false;
  }
}
